import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Album, Song } from '../types';
import { albumsAPI, songsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SearchResults = styled.div<{ show: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--dark-bg);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.show ? 'block' : 'none'};
  margin-top: 4px;
`;

const ResultItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ResultIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const SearchBar: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ albums: Album[], songs: Song[] }>({ albums: [], songs: [] });
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const { playSong } = usePlayer();

  useEffect(() => {
    if (query.length > 2) {
      searchContent();
    } else {
      setResults({ albums: [], songs: [] });
      setShowResults(false);
    }
  }, [query]);

  const searchContent = async () => {
    try {
      const [albumsResponse, songsResponse] = await Promise.all([
        albumsAPI.getAll(),
        // Buscar todas as músicas (implementar endpoint de busca depois)
        Promise.resolve({ data: [] as Song[] })
      ]);

      const filteredAlbums = albumsResponse.data.filter(album =>
        album.name.toLowerCase().includes(query.toLowerCase()) ||
        (album.description && album.description.toLowerCase().includes(query.toLowerCase()))
      );

      setResults({ albums: filteredAlbums, songs: [] });
      setShowResults(true);
    } catch (error) {
      console.error('Erro na busca:', error);
    }
  };

  const handleResultClick = (type: 'album' | 'song', item: Album | Song) => {
    if (type === 'album') {
      navigate(`/album/${item.id}`);
    } else {
      playSong(item as Song, [item as Song]);
    }
    setShowResults(false);
    setQuery('');
  };

  return (
    <SearchContainer>
      <SearchInput
        type="text"
        placeholder="🔍 Buscar álbuns, músicas..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => query.length > 2 && setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 200)}
      />
      
      <SearchResults show={showResults}>
        {results.albums.map(album => (
          <ResultItem key={`album-${album.id}`} onClick={() => handleResultClick('album', album)}>
            <ResultIcon>💿</ResultIcon>
            <div>
              <div style={{ fontWeight: 600 }}>{album.name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Álbum</div>
            </div>
          </ResultItem>
        ))}
        
        {results.albums.length === 0 && query.length > 2 && (
          <ResultItem>
            <div style={{ color: 'var(--text-muted)' }}>Nenhum resultado encontrado</div>
          </ResultItem>
        )}
      </SearchResults>
    </SearchContainer>
  );
};

export default SearchBar;